import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from 'src/app/core/services/auth.service';
import { AppProfile } from '../../../shared/app-profile/app-profile';

export interface SubOrganization {
  id: string;
  parentOrganizationId: string;
  name: string;
  department: string;
  description?: string;
  managerName?: string;
  managerEmail?: string;
  contactPhone?: string;
  budget?: number;
  status: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface UpdateSubOrganizationRequest {
  orgId: string;
  name: string;
  parentOrganizationId: string;
  address: {
    address1: string;
    address2: string;
    country: string;
    city: string;
    state: string;
    postalCode: string;
    phone: string;
  };
  contactInfo: {
    firstName: string;
    lastName: string;
    pointOfContactDetails?: string;
  };
  active: boolean;
  excludeReferrals: boolean;
}

export interface ParentOrganization {
  id: string;
  name: string;
  type: string;
}

export interface OrganizationStructureChild {
  selectedOrgIds: any[];
  active: boolean;
  locationFederated: boolean;
  children: any[];
  name: string;
  displayName: string;
  alias: string | null;
  orgId: string;
  parentOrgId: string;
  selectable: boolean;
  selected: boolean;
}

export interface SubOrganizationDetails {
  orgId: string;
  name: string;
  displayName: string;
  address: {
    address1: string;
    address2: string;
    country: string;
    city: string;
    state: string;
    postalCode: string;
    phone: string;
  };
  contactInfo: {
    firstName: string;
    lastName: string;
  };
  active: boolean;
  excludeReferrals: boolean;
  parentOrganizationId: string;
  timezone: string | null;
  createdBy: string;
  updatedBy: string | null;
}

export interface OrganizationStructureResponse {
  selectedOrgIds: any[];
  active: boolean;
  locationFederated: boolean;
  children: OrganizationStructureChild[];
  name: string;
  displayName: string;
  alias: string | null;
  orgId: string;
  parentOrgId: string | null;
  selectable: boolean;
  selected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class EditSubOrganizationService {
  constructor(
    private readonly http: HttpClient, 
    private readonly authService: AuthService,
    private readonly appProfile: AppProfile
  ) {}

  getSubOrganization(id: string, token: string): Observable<SubOrganization> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<SubOrganization>(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs/${id}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  getParentOrganization(id: string, token: string): Observable<ParentOrganization> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<ParentOrganization>(`${this.appProfile.epUs}/organization-ms/organization/${id}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  updateSubOrganization(subOrganizationData: UpdateSubOrganizationRequest, token: string): Observable<any> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.put(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs`, subOrganizationData, { headers }).pipe(
      map(response => {
        return { success: true, data: response };
      }),
      catchError(this.handleError)
    );
  }

  getDepartments(token: string): Observable<string[]> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<string[]>(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs/departments`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  getOrganizationStructure(orgId: string, token: string): Observable<OrganizationStructureResponse> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<OrganizationStructureResponse>(`${this.appProfile.epUs}/organization-ms/org-structure/${orgId}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  getSubOrganizationById(subOrgId: string, token: string): Observable<SubOrganizationDetails> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<SubOrganizationDetails>(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs/${subOrgId}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  getSubOrganizationsByParent(parentId: string, token: string): Observable<SubOrganization[]> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<SubOrganization[]>(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs/by-parent/${parentId}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  validateSubOrganizationName(name: string, parentId: string, token: string, excludeId?: string): Observable<boolean> {
    const params = excludeId ? { excludeId } : {};
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.post<{ isValid: boolean }>(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs/validate-name`, 
      { name, parentId }, 
      { headers, params }
    ).pipe(
      map(response => response.isValid),
      catchError(this.handleError)
    );
  }

  getSubOrganizationsByDepartment(department: string, token: string): Observable<SubOrganization[]> {
    const headers = this.authService.getCommonHeaders(token, '1.0.0');
    return this.http.get<SubOrganization[]>(`${this.appProfile.epUs}/organization-ms/organization/sub-orgs/by-department/${department}`, { headers }).pipe(
      catchError(this.handleError)
    );
  }

  private readonly handleError = (error: any): Observable<never> => {
    const errorMessage = this.getErrorMessage(error);
    console.error('EditSubOrganizationService Error:', error);
    return throwError(() => ({ message: errorMessage, status: error.status }));
  };

  private getErrorMessage(error: any): string {
    if (error.error instanceof ErrorEvent) {
      return error.error.message;
    }

    return this.getHttpErrorMessage(error);
  }

  private getHttpErrorMessage(error: any): string {
    const statusErrorMap: Record<number, string> = {
      400: error.error?.message || 'Bad request - please check your input',
      401: 'Unauthorized - please log in again',
      403: 'Forbidden - you do not have permission to perform this action',
      404: 'Sub-organization not found',
      409: 'Sub-organization name already exists in this parent organization'
    };

    if (statusErrorMap[error.status]) {
      return statusErrorMap[error.status];
    }

    if (error.status >= 500) {
      return 'Server error - please try again later';
    }

    return error.error?.message || `Error: ${error.status}`;
  }
}